/**
 * Semantic validation for the dynamic packet mapper DSL (ng-bridge-mqtt).
 *
 * This mirrors, rule for rule, the runtime validator that the bridge itself uses:
 *   monorepo/libs/niotix-grid/persistence/mapper-payload/src/lib/validation.ts
 *   monorepo/libs/niotix-grid/persistence/mapper-payload/src/lib/path-selector.ts (assertSafePath)
 *   monorepo/libs/niotix-grid/persistence/mapper-payload/src/lib/topic-value.ts
 *
 * The Nx library cannot be imported here: the frontend image is built with `frontend/`
 * as its Docker context (see docker-bake.hcl), so `../monorepo` does not exist at build
 * time. `__tests__/mapperConfigValidation.spec.js` hashes the upstream files and fails
 * when they change, so this copy cannot drift unnoticed the way the old Monaco JSON
 * schema did.
 */

const UNSAFE_PATH_SEGMENTS = ['__proto__', 'prototype', 'constructor'];
const SAFE_PATH_SEGMENT = /^[A-Za-z0-9_-]+$/;

export class MapperConfigError extends Error {
  constructor(message) {
    super(message);
    this.name = 'MapperConfigError';
  }
}

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function assertKeys(value, allowed, location) {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) {
      throw new MapperConfigError(`Unknown field '${key}' at ${location}`);
    }
  }
}

export function assertSafePath(path) {
  if (path.length > 256) {
    throw new MapperConfigError(`unsafe path: ${path}`);
  }

  const normalized = path.replace(/^\$(root|item|parent|captures)?\.?/, '');
  if (normalized.length === 0 || normalized === '[]') {
    return;
  }

  for (const segment of normalized.split('.')) {
    const clean = segment.replace(/\[\]$/g, '');
    if (!clean || UNSAFE_PATH_SEGMENTS.includes(clean) || !SAFE_PATH_SEGMENT.test(clean)) {
      throw new MapperConfigError(`unsafe path: ${path}`);
    }
  }
}

function topicTextError(value) {
  let bytes = 0;
  for (const character of value) {
    const code = character.codePointAt(0);
    bytes += code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
  }
  if (bytes > 65535) {
    return 'topic exceeds 65535 UTF-8 bytes';
  }
  if (/[+#]/u.test(value)) {
    return 'topic contains a wildcard';
  }
  if (/\{\{|\}\}/u.test(value)) {
    return 'topic contains an unresolved reference';
  }
  for (const character of value) {
    const code = character.codePointAt(0);
    if (
      code <= 0x1f ||
      (code >= 0x7f && code <= 0x9f) ||
      (code >= 0xd800 && code <= 0xdfff) ||
      (code >= 0xfdd0 && code <= 0xfdef) ||
      (code & 0xffff) >= 0xfffe
    ) {
      return 'topic contains invalid MQTT UTF-8';
    }
  }
  return undefined;
}

function topicError(value) {
  if (typeof value !== 'string') {
    return 'topic must be a string';
  }
  if (value.split('/').some((level) => !level.trim())) {
    return 'topic contains an empty level';
  }
  return topicTextError(value);
}

function validateTopicOperand(value, lookups, depth) {
  if (depth > 32) {
    throw new MapperConfigError('Topic mapping is too deeply nested');
  }
  if (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  ) {
    return;
  }
  if (!isRecord(value) || !['path', 'capture', 'lookup'].some((key) => key in value)) {
    throw new MapperConfigError('topicParts requires literals or scalar value selectors');
  }
  if (
    Array.isArray(value.transforms) &&
    value.transforms.some((transform) => isRecord(transform) && 'default' in transform)
  ) {
    throw new MapperConfigError('Required topic fields must not use fallback transforms');
  }
  if ('capture' in value && typeof value.capture !== 'string') {
    throw new MapperConfigError('Topic capture must be a string');
  }
  if (isRecord(value.lookup)) {
    const lookup = value.lookup;
    assertKeys(lookup, ['table', 'from', 'key'], 'topic lookup');
    const hasFrom = 'from' in lookup;
    const hasKey = 'key' in lookup;
    if (hasFrom === hasKey) {
      throw new MapperConfigError('Topic lookup requires exactly one key or from');
    }
    validateTopicOperand(lookup.from ?? lookup.key, lookups, depth + 1);
  }
  validateValue(value, lookups, depth + 1);
}

function validateTopicConstruction(spec, lookups, depth) {
  assertKeys(spec, ['topicParts'], 'topic construction');
  if (!Array.isArray(spec.topicParts) || !spec.topicParts.length) {
    throw new MapperConfigError('topicParts must be a non-empty array');
  }
  for (const part of spec.topicParts) {
    if (typeof part === 'string') {
      const reason = topicTextError(part);
      if (!part.trim() || reason) {
        throw new MapperConfigError(reason ?? 'Empty topic literal');
      }
    }
    validateTopicOperand(part, lookups, depth + 1);
  }
  const skeleton = spec.topicParts.map((part) => (isRecord(part) ? 'x' : String(part))).join('');
  const reason = topicError(skeleton);
  if (reason) {
    throw new MapperConfigError(reason);
  }
}

function validateValue(value, lookups, depth = 0) {
  if (depth > 32) {
    throw new MapperConfigError('Value mapping is too deeply nested');
  }
  if (value === undefined || value === null || typeof value !== 'object' || Array.isArray(value)) {
    return;
  }
  const spec = value;
  if ('topicParts' in spec) {
    validateTopicConstruction(spec, lookups, depth);
  } else if ('path' in spec) {
    assertKeys(spec, ['path', 'transforms'], 'value path');
    if (typeof spec.path !== 'string') {
      throw new MapperConfigError('path must be a string');
    }
    assertSafePath(spec.path);
  } else if ('capture' in spec) {
    assertKeys(spec, ['capture', 'transforms'], 'value capture');
  } else if ('lookup' in spec) {
    assertKeys(spec, ['lookup', 'transforms'], 'value lookup');
    if (!isRecord(spec.lookup) || typeof spec.lookup.table !== 'string') {
      throw new MapperConfigError('lookup table is required');
    }
    if (!isRecord(lookups) || !isRecord(lookups[spec.lookup.table])) {
      throw new MapperConfigError(`Unknown lookup table '${spec.lookup.table}'`);
    }
    validateValue(spec.lookup.from, lookups, depth + 1);
  } else {
    for (const child of Object.values(spec)) {
      validateValue(child, lookups, depth + 1);
    }
  }

  if (Array.isArray(spec.transforms)) {
    for (const transform of spec.transforms) {
      if (typeof transform === 'object' && transform !== null && 'scalePow10' in transform) {
        validateValue(transform.scalePow10, lookups, depth + 1);
      }
      if (
        isRecord(transform) &&
        'timestampEpoch' in transform &&
        (Object.keys(transform).length !== 1 ||
          !['seconds', 'milliseconds'].some((unit) => unit === transform.timestampEpoch))
      ) {
        throw new MapperConfigError('timestampEpoch requires seconds or milliseconds');
      }
    }
  }
}

function validateDirect(output, lookups) {
  if (!isRecord(output)) {
    throw new MapperConfigError('direct output is required');
  }
  assertKeys(output, ['deviceId', 'dtwinId', 'updates'], 'direct output');
  validateValue(output.deviceId, lookups);
  validateValue(output.dtwinId, lookups);
  if (!Array.isArray(output.updates) || output.updates.length === 0) {
    throw new MapperConfigError('direct updates must be a non-empty array');
  }
  for (const update of output.updates) {
    if (!isRecord(update)) {
      throw new MapperConfigError('direct update must be an object');
    }
    assertKeys(update, ['stateId', 'value', 'time', 'valueType'], 'direct update');
    validateValue(update.stateId, lookups);
    validateValue(update.value, lookups);
    validateValue(update.time, lookups);
    validateValue(update.valueType, lookups);
  }
}

function validateDdr(output, lookups) {
  if (!isRecord(output)) {
    throw new MapperConfigError('ddr output is required');
  }
  assertKeys(output, ['deviceId', 'routing', 'variable', 'data'], 'ddr output');
  validateValue(output.deviceId, lookups);
  validateValue(output.routing, lookups);
  validateValue(output.variable, lookups);
  if (!isRecord(output.data)) {
    throw new MapperConfigError('ddr data is required');
  }
  assertKeys(output.data, ['value', 'time', 'valueType', 'unit', 'quality', 'sourceStatus'], 'ddr data');
  validateValue(output.data.value, lookups);
  validateValue(output.data.time, lookups);
  validateValue(output.data.valueType, lookups);
  validateValue(output.data.unit, lookups);
  validateValue(output.data.quality, lookups);
  validateValue(output.data.sourceStatus, lookups);
}

function validateEmitRule(rule, output, lookups) {
  if (!isRecord(rule)) {
    throw new MapperConfigError('emit rule must be an object');
  }
  assertKeys(rule, ['foreach', 'output'], 'emit rule');
  if (typeof rule.foreach === 'string') {
    assertSafePath(rule.foreach);
  }
  if (!isRecord(rule.output)) {
    throw new MapperConfigError('emit output must be an object');
  }
  assertKeys(rule.output, ['direct', 'ddr'], 'emit output');
  if (output === 'direct') {
    validateDirect(rule.output.direct, lookups);
  } else {
    validateDdr(rule.output.ddr, lookups);
  }
}

function validateLookups(lookups) {
  if (lookups === undefined) {
    return;
  }
  if (!isRecord(lookups)) {
    throw new MapperConfigError('lookups must be an object');
  }
  for (const table of Object.values(lookups)) {
    if (!isRecord(table)) {
      throw new MapperConfigError('lookup tables must be objects');
    }
  }
}

function validateInput(input) {
  if (input === undefined) {
    return;
  }
  if (!isRecord(input)) {
    throw new MapperConfigError('input must be an object');
  }
  assertKeys(input, ['root', 'topicPattern', 'topicCaptures'], 'input');
  if (typeof input.root === 'string') {
    assertSafePath(input.root);
  }
  if (typeof input.topicPattern === 'string') {
    if (input.topicPattern.length > 256) {
      throw new MapperConfigError('topicPattern is too long');
    }
    try {
      new RegExp(input.topicPattern);
    } catch (_) {
      throw new MapperConfigError('Invalid topicPattern');
    }
  }
  if (input.topicCaptures !== undefined) {
    if (!isRecord(input.topicCaptures)) {
      throw new MapperConfigError('topicCaptures must be an object');
    }
    for (const [name, index] of Object.entries(input.topicCaptures)) {
      if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) {
        throw new MapperConfigError(`topicCaptures.${name} must be a non-negative integer`);
      }
    }
  }
}

/**
 * Throws a MapperConfigError describing the first semantic problem, otherwise
 * returns the config unchanged.
 */
export function validateMapperConfig(config) {
  if (!isRecord(config)) {
    throw new MapperConfigError('Config must be an object');
  }
  assertKeys(config, ['version', 'output', 'input', 'lookups', 'emit'], 'config');
  if (config.version !== 1) {
    throw new MapperConfigError('Unsupported version');
  }
  if (config.output !== 'direct' && config.output !== 'ddr') {
    throw new MapperConfigError('Unsupported output mode');
  }
  if (!Array.isArray(config.emit)) {
    throw new MapperConfigError('emit must be an array');
  }
  validateInput(config.input);
  validateLookups(config.lookups);
  for (const rule of config.emit) {
    validateEmitRule(rule, config.output, config.lookups);
  }
  return config;
}

/** Returns the validation error message for a config, or an empty string when it is valid. */
export function mapperConfigErrorMessage(config) {
  try {
    validateMapperConfig(config);
    return '';
  } catch (error) {
    return error.message || 'Invalid mapper config';
  }
}
